import type { Room, RoomFinishSettings, RoomSetting, WallFinish } from "../types";
import { lookById, type Look } from "./catalog";

/**
 * Pure edits to `room.finishes`, shared by the store actions. Each returns
 * the same `room` object when nothing changes so callers can skip the
 * history entry.
 */

export type FinishesPatch = { [K in keyof RoomFinishSettings]?: RoomFinishSettings[K] | null };

const SURFACE_KEYS: (keyof RoomFinishSettings)[] = ["floor", "walls", "wallOverrides", "ceiling", "trimColor"];

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function withFinishes(room: Room, next: RoomFinishSettings): Room {
  const clean: RoomFinishSettings = { ...next };
  if (clean.wallOverrides && Object.keys(clean.wallOverrides).length === 0) delete clean.wallOverrides;
  if (clean.setting === "indoor") delete clean.setting;
  for (const k of Object.keys(clean) as (keyof RoomFinishSettings)[]) {
    if (clean[k] === undefined) delete clean[k];
  }
  if (sameJson(room.finishes ?? {}, clean)) return room;
  const out: Room = { ...room };
  if (Object.keys(clean).length > 0) out.finishes = clean;
  else delete out.finishes;
  return out;
}

/**
 * Apply `patch` (`null` removes a field, so the surface goes back to its
 * inferred finish). A manual change to any surface clears the applied Look.
 */
export function patchRoomFinishes(room: Room, patch: FinishesPatch): Room {
  const next: Record<string, unknown> = { ...(room.finishes ?? {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined) delete next[k];
    else next[k] = v;
  }
  const touchesSurface = SURFACE_KEYS.some((k) => k in patch && !sameJson(patch[k] ?? undefined, room.finishes?.[k]));
  if (!("look" in patch) && touchesSurface) delete next.look;
  return withFinishes(room, next as RoomFinishSettings);
}

/** Set (or with `null` clear) one wall's finish. */
export function setWallFinishIn(room: Room, key: string, finish: WallFinish | null): Room {
  const overrides = { ...(room.finishes?.wallOverrides ?? {}) };
  if (finish) overrides[key] = { type: finish.type, color: finish.color };
  else delete overrides[key];
  return patchRoomFinishes(room, { wallOverrides: Object.keys(overrides).length > 0 ? overrides : null });
}

/** A Look sets floor, walls, ceiling and trim together; per-wall choices stay. */
export function applyLookTo(room: Room, look: Look): Room {
  return patchRoomFinishes(room, {
    look: look.id,
    setting: look.setting,
    floor: { ...look.floor },
    walls: { ...look.walls },
    ceiling: { ...look.ceiling },
    trimColor: look.trimColor,
  });
}

/** Indoor / Outdoor. Nothing chosen for the other setting is thrown away. */
export function setRoomSettingIn(room: Room, setting: RoomSetting): Room {
  const look = lookById(room.finishes?.look);
  return patchRoomFinishes(room, {
    setting,
    ...(look && look.setting !== setting ? { look: null } : {}),
  });
}
